import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDF_DOCUMENT_OPTIONS } from '../utils/pdfSafety';
import { fabric } from 'fabric';
import { supabase } from '../auth/supabaseClient';
import OcrWordOverlay from './OcrWordOverlay';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './SimplePDFViewer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const ANNOTATION_COLORS = {
  yellow: '#FFEB3B',
  red: '#F44336',
  blue: '#2196F3',
  green: '#4CAF50',
  black: '#212121',
};

const STAMP_CONFIGS = {
  approved:    { label: 'APPROVED',    color: '#4CAF50', textColor: '#fff' },
  rejected:    { label: 'REJECTED',    color: '#F44336', textColor: '#fff' },
  duplicate:   { label: 'DUPLICATE',   color: '#FF9800', textColor: '#fff' },
  for_review:  { label: 'FOR REVIEW',  color: '#2196F3', textColor: '#fff' },
  void:        { label: 'VOID',        color: '#9C27B0', textColor: '#fff' },
};

const TOOLS = {
  SELECT:     'select',
  STICKY:     'sticky',
  HIGHLIGHT:  'highlight',
  REDACT:     'redact',
  TEXTBOX:    'textbox',
  STAMP:      'stamp',
  FREEHAND:   'freehand',
  ARROW:      'arrow',
  CORRECT:    'correct',
};

const PAGE_ROLES = [
  { key: 'cover_sheet',       label: 'Cover Sheet',        icon: '📄' },
  { key: 'line_items',        label: 'Line Items',          icon: '📋' },
  { key: 'summary',           label: 'Summary',             icon: '📊' },
  { key: 'attachments',       label: 'Attachments',         icon: '📎' },
  { key: 'terms_conditions',  label: 'Terms & Conditions',  icon: '⚖️' },
  { key: 'signature_page',    label: 'Signature Page',      icon: '✍️' },
  { key: 'continuation',      label: 'Continuation',        icon: '➡️' },
  { key: 'unclassified',      label: 'Unclassified',        icon: '❓' },
];

// Auto-save delay after an annotation change settles
const AUTOSAVE_DELAY_MS = 1500;

export default function SimplePDFViewer({
  fileUrl,
  documentId,
  // Base URL for capture-service calls. DEFAULTS TO '' AND '' IS VALID: every
  // caller in this app passes process.env.REACT_APP_CAPTURE_API_BASE_URL || '',
  // and that env var is deliberately empty in .env.development/.env.production
  // because capture-ui is served same-origin (Caddy proxies /api/capture/* to
  // :4010; CRA's setupProxy does the same in dev). Treating this value as a
  // BOOLEAN is what silently disabled every annotation call — see the guards
  // below and vault bugs/shared/2026-08-17-capture-annotations-not-transferred-
  // on-publish.md.
  captureApiBase = '',
  authToken,
  tenantId,
  onPageChange,
  onSplitDelete,
  onAppend,
  onCorrectionDrawn,
  // Click-to-correct mode (5-B): when true and the CORRECT tool is active and
  // rotation === 0, an OCR-word overlay is rendered per page above the fabric
  // canvas; single-clicking a word fires onTokenClicked with normalized bbox +
  // page. Rotation is gated because OCR bboxes are stored in the un-rotated
  // coordinate space.
  clickCorrectMode = false,
  onTokenClicked,
  onRotationChange,
  // When non-null, the OCR-word click-to-select overlay is armed (per page,
  // rotation === 0) so the user can click the correct value token for that
  // field. Replaces the old `✎` toolbar tool as the sole arming signal — the
  // parent (ReviewDetailPage) sets it from the right-side per-field button.
  correctingFieldKey = null,
  // Human-readable label for the armed field, shown in the armed-state banner.
  correctingFieldLabel = null,
}) {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [rotation, setRotation] = useState(0);

  // Annotation state
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [activeColor, setActiveColor] = useState('yellow');
  const [activeStamp, setActiveStamp] = useState('approved');
  const [showStampPicker, setShowStampPicker] = useState(false);
  const [dirtyPages, setDirtyPages] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  // Last save failure, surfaced in the toolbar next to the Save button. Null
  // when the last attempt succeeded or none has run.
  const [saveError, setSaveError] = useState(null);
  // Guard-rejection logging is once-per-reason, not once-per-call: the
  // debounced auto-save re-fires on every dirty change and would otherwise
  // spam the console for a document that is simply missing an id.
  const guardLoggedRef = useRef({});

  // Page role state
  const [pageRoles, setPageRoles] = useState({});
  const [showRolePicker, setShowRolePicker] = useState(null);

  // Refs for the layout + per-page state
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);  // .spv-viewer-main (scroll context)
  const pageWrapsRef = useRef({});          // { [page]: .spv-page-wrap DIV }
  const fabricElsRef = useRef({});          // { [page]: fabric wrapper DIV }
  const fabricCanvasesRef = useRef({});     // { [page]: fabric.Canvas }
  const pageDimsRef = useRef({});           // { [page]: { width, height } }
  const annotationsByPageRef = useRef({});  // { [page]: annotation[] from API }
  const hydratedPagesRef = useRef(new Set()); // pages whose canvas has been hydrated from API
  // Stable per-page ref objects for OcrWordOverlay (whose useEffect has the
  // ref object itself as a dep; passing a fresh object every render thrashes
  // the effect and its MutationObserver/network fetches).
  const pageSurfaceRefObjectsRef = useRef({});
  const getPageSurfaceRefObject = (pg) => {
    if (!pageSurfaceRefObjectsRef.current[pg]) {
      pageSurfaceRefObjectsRef.current[pg] = { current: null };
    }
    return pageSurfaceRefObjectsRef.current[pg];
  };

  // Drag state — shared across canvases. dragStartPageRef tracks which page
  // owns the in-progress shape so document-level mousemove/mouseup can clamp.
  const isDrawingRef = useRef(false);
  const startPointRef = useRef(null);
  const activeShapeRef = useRef(null);
  const dragStartPageRef = useRef(null);

  // Tool/color/stamp via refs so long-lived per-canvas handlers read latest
  const activeToolRef = useRef(activeTool);
  const activeColorRef = useRef(activeColor);
  const activeStampRef = useRef(activeStamp);
  useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);
  useEffect(() => { activeColorRef.current = activeColor; }, [activeColor]);
  useEffect(() => { activeStampRef.current = activeStamp; }, [activeStamp]);

  // Last interacted canvas — used to scope keyboard delete to one page
  const lastActiveCanvasPageRef = useRef(null);

  // Native PDF width (for auto-fit on container resize). Captured on first page.
  const nativePdfWidthRef = useRef(null);

  // Debounce timer for auto-save
  const saveTimerRef = useRef(null);

  // ── Notify parent of rotation changes (5-B hint) ────────────────────────
  useEffect(() => {
    if (onRotationChange) onRotationChange(rotation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotation]);

  // ── Auth headers ────────────────────────────────────────────────────────
  const getAuthHeaders = useCallback(async () => {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch { /* non-fatal */ }
    if (tenantId) headers['x-tenant-id'] = tenantId;
    return headers;
  }, [tenantId]);

  // ── Guard diagnostics ───────────────────────────────────────────────────
  // The annotation calls used to bail on `!documentId || !captureApiBase` with
  // NO logging on any branch, before setSaving/try/fetch — so a rejected save
  // produced no request, no console output and no error state, and the dirty
  // flag never cleared (the Save button stuck on "Save" forever). Whatever the
  // reason, a refusal to talk to the server now says so exactly once.
  const logGuard = useCallback((where, reason) => {
    const key = `${where}:${reason}`;
    if (guardLoggedRef.current[key]) return;
    guardLoggedRef.current[key] = true;
    console.warn(`[viewer] ${where} skipped — ${reason} (logged once)`);
  }, []);

  // ── Save page role to API ───────────────────────────────────────────────
  const savePageRole = useCallback(async (page, role) => {
    if (!documentId) { logGuard('savePageRole', 'no documentId'); return; }
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${captureApiBase}/api/capture/processing-monitor/documents/${documentId}/page-role`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ page_number: page, role }),
        }
      );
      // Same swallowed-response shape as savePageAnnotations below: a 401/403/
      // 500 used to update local state as though the write had landed.
      if (!res.ok) {
        console.error(`[viewer] save page role FAILED page=${page} status=${res.status}`);
        setSaveError(`Page role not saved (${res.status})`);
        return;
      }
      setPageRoles(prev => ({ ...prev, [page]: role }));
    } catch (err) {
      console.error('[viewer] save page role error:', err);
      setSaveError('Page role not saved (network error)');
    }
  }, [documentId, captureApiBase, getAuthHeaders, logGuard]);

  // ── Load all annotations + page roles once per document ─────────────────
  const loadAllAnnotations = useCallback(async () => {
    if (!documentId) { logGuard('loadAllAnnotations', 'no documentId'); return; }
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${captureApiBase}/api/capture/processing-monitor/documents/${documentId}/annotations`,
        { headers }
      );
      const data = await res.json();
      if (data.ok && data.annotations) {
        const byPage = {};
        const roles = {};
        data.annotations.forEach(a => {
          if (a.annotation_type === 'page_role') {
            roles[a.page_number] = a.metadata?.role;
            return;
          }
          if (!byPage[a.page_number]) byPage[a.page_number] = [];
          byPage[a.page_number].push(a);
        });
        annotationsByPageRef.current = byPage;
        setPageRoles(roles);
        // Hydrate any canvases that exist but haven't been populated yet
        Object.keys(fabricCanvasesRef.current).forEach(pageKey => {
          const page = Number(pageKey);
          if (hydratedPagesRef.current.has(page)) return;
          const fc = fabricCanvasesRef.current[page];
          if (fc) hydrateCanvas(fc, page);
        });
      }
    } catch (err) {
      console.error('[viewer] load annotations error:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, captureApiBase, getAuthHeaders, logGuard]);

  useEffect(() => {
    if (documentId) loadAllAnnotations();
  }, [documentId, loadAllAnnotations]);

  // ── Mark a page as dirty (triggers debounced auto-save) ─────────────────
  const markPageDirty = useCallback((page) => {
    setDirtyPages(prev => {
      if (prev.has(page)) return prev;
      const next = new Set(prev);
      next.add(page);
      return next;
    });
  }, []);

  // ── Save a single page's annotations to the API ─────────────────────────
  const savePageAnnotations = useCallback(async (page) => {
    const fc = fabricCanvasesRef.current[page];
    // `captureApiBase` is NOT part of this test — '' is its correct value when
    // capture-ui is served same-origin, and treating it as a boolean is what
    // made every save a silent no-op.
    if (!fc) { logGuard('savePageAnnotations', `no fabric canvas for page ${page}`); return; }
    if (!documentId) { logGuard('savePageAnnotations', 'no documentId'); return; }
    setSaving(true);
    try {
      const objects = fc.getObjects();
      const annotations = objects
        // Don't persist field-correction marquees — they're transient UI
        .filter(obj => obj.annotationType !== TOOLS.CORRECT)
        .map(obj => ({
          annotation_type: obj.annotationType || 'unknown',
          fabric_object: obj.toJSON(['annotationType', 'stampType', 'noteText']),
          metadata: obj.annotationMeta || null,
        }));
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${captureApiBase}/api/capture/processing-monitor/documents/${documentId}/annotations`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ page_number: page, annotations }),
        }
      );

      // The response was previously not inspected at all: the dirty flag was
      // cleared unconditionally, so a 401/403/500 looked exactly like a
      // successful save and the user's work was silently discarded. The page
      // STAYS dirty on failure so the Save button remains available to retry.
      if (!res.ok) {
        let detail = '';
        try {
          const body = await res.json();
          detail = body?.error ? `: ${body.error}` : '';
        } catch { /* non-JSON error body — status alone is the signal */ }
        console.error(
          `[viewer] save annotations FAILED page=${page} status=${res.status}${detail}`,
        );
        setSaveError(`Not saved (${res.status})`);
        return;
      }

      setSaveError(null);
      setDirtyPages(prev => {
        if (!prev.has(page)) return prev;
        const next = new Set(prev);
        next.delete(page);
        return next;
      });
    } catch (err) {
      // Network/abort: also NOT a success — keep the page dirty and say so.
      console.error('[viewer] save annotations error:', err);
      setSaveError('Not saved (network error)');
    } finally {
      setSaving(false);
    }
  }, [documentId, captureApiBase, getAuthHeaders, logGuard]);

  // ── Debounced auto-save when dirtyPages changes ─────────────────────────
  useEffect(() => {
    if (dirtyPages.size === 0) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const pages = Array.from(dirtyPages);
    saveTimerRef.current = setTimeout(() => {
      pages.forEach(p => { savePageAnnotations(p); });
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [dirtyPages, savePageAnnotations]);

  // ── Hydrate a canvas from cached annotations ────────────────────────────
  const hydrateCanvas = useCallback((fc, page) => {
    if (hydratedPagesRef.current.has(page)) return;
    const pageAnnotations = annotationsByPageRef.current[page] || [];
    if (pageAnnotations.length === 0) {
      hydratedPagesRef.current.add(page);
      return;
    }
    // Suppress dirty-marking while we add saved objects
    fc._hydrating = true;
    pageAnnotations.forEach(a => {
      fabric.util.enlivenObjects([a.fabric_object], (objs) => {
        objs.forEach(obj => {
          obj.annotationType = a.annotation_type;
          obj.annotationMeta = a.metadata;
          fc.add(obj);
        });
        fc.renderAll();
      });
    });
    // Defer flag clear so any synchronous object:added events finish first
    setTimeout(() => { fc._hydrating = false; }, 0);
    hydratedPagesRef.current.add(page);
  }, []);

  // ── Apply current tool state to a fabric canvas ─────────────────────────
  const applyToolState = useCallback((fc) => {
    if (!fc) return;
    const tool = activeToolRef.current;
    const color = activeColorRef.current;
    fc.isDrawingMode = tool === TOOLS.FREEHAND;
    if (tool === TOOLS.FREEHAND) {
      fc.freeDrawingBrush.color = ANNOTATION_COLORS[color] || '#000';
      fc.freeDrawingBrush.width = 3;
      fc.freeDrawingBrush.annotationType = TOOLS.FREEHAND;
    }
    if (tool === TOOLS.SELECT) {
      fc.selection = true;
      fc.defaultCursor = 'default';
    } else if (tool !== TOOLS.FREEHAND) {
      fc.selection = false;
      fc.defaultCursor = 'crosshair';
    }
  }, []);

  // ── Per-canvas mouse-down handler (drag tools + placement tools) ────────
  // mouse:move and mouse:up are handled at document level (see below) so a
  // drag started on page N can clamp when the cursor leaves page N's canvas.
  const bindCanvasHandlers = useCallback((page, fc) => {
    const onMouseDown = (opt) => {
      lastActiveCanvasPageRef.current = page;
      const tool = activeToolRef.current;
      const color = ANNOTATION_COLORS[activeColorRef.current] || '#FFEB3B';

      // Placement tools — single click creates an object and reverts to SELECT
      if (tool === TOOLS.STAMP) {
        const target = fc.findTarget(opt.e);
        if (target) return;
        const ptr = fc.getPointer(opt.e);
        const cfg = STAMP_CONFIGS[activeStampRef.current];
        const rect = new fabric.Rect({
          width: 160, height: 48, fill: cfg.color, rx: 4, ry: 4, opacity: 0.9,
        });
        const text = new fabric.Text(cfg.label, {
          fontSize: 20, fill: cfg.textColor, fontWeight: 'bold',
          originX: 'center', originY: 'center', left: 80, top: 24,
        });
        const group = new fabric.Group([rect, text], {
          left: ptr.x - 80, top: ptr.y - 24,
          selectable: true,
          annotationType: TOOLS.STAMP,
          annotationMeta: { stampType: activeStampRef.current },
        });
        fc.add(group);
        fc.setActiveObject(group);
        markPageDirty(page);
        setActiveTool(TOOLS.SELECT);
        return;
      }

      if (tool === TOOLS.STICKY) {
        const target = fc.findTarget(opt.e);
        if (target) return;
        const ptr = fc.getPointer(opt.e);
        const rect = new fabric.Rect({
          left: 0, top: 0, width: 180, height: 100,
          fill: '#FFEB3B', opacity: 0.92, rx: 4, ry: 4,
        });
        const text = new fabric.IText('Double-click to edit...', {
          left: 8, top: 8, fontSize: 12, fill: '#333', width: 164,
        });
        const group = new fabric.Group([rect, text], {
          left: ptr.x, top: ptr.y,
          selectable: true, hasControls: true,
          annotationType: TOOLS.STICKY,
        });
        fc.add(group);
        fc.setActiveObject(group);
        markPageDirty(page);
        setActiveTool(TOOLS.SELECT);
        return;
      }

      if (tool === TOOLS.TEXTBOX) {
        const target = fc.findTarget(opt.e);
        if (target) return;
        const ptr = fc.getPointer(opt.e);
        const tb = new fabric.IText('Type here...', {
          left: ptr.x, top: ptr.y,
          fontSize: 14, fill: color,
          backgroundColor: 'rgba(255,255,255,0.85)',
          padding: 4,
          annotationType: TOOLS.TEXTBOX,
        });
        fc.add(tb);
        fc.setActiveObject(tb);
        tb.enterEditing();
        tb.selectAll();
        markPageDirty(page);
        setActiveTool(TOOLS.SELECT);
        return;
      }

      // Drag tools (HIGHLIGHT, REDACT, ARROW, CORRECT). SELECT and FREEHAND
      // are handled natively by fabric.
      if ([TOOLS.SELECT, TOOLS.FREEHAND].includes(tool)) return;

      const target = fc.findTarget(opt.e);
      if (target) return;

      const ptr = fc.getPointer(opt.e);
      isDrawingRef.current = true;
      startPointRef.current = { x: ptr.x, y: ptr.y };
      dragStartPageRef.current = page;

      let shape;
      if (tool === TOOLS.HIGHLIGHT) {
        shape = new fabric.Rect({
          left: ptr.x, top: ptr.y, width: 0, height: 0,
          fill: color, opacity: 0.35, selectable: true,
          annotationType: TOOLS.HIGHLIGHT,
        });
      } else if (tool === TOOLS.REDACT) {
        shape = new fabric.Rect({
          left: ptr.x, top: ptr.y, width: 0, height: 0,
          fill: '#000000', opacity: 1, selectable: true,
          annotationType: TOOLS.REDACT,
        });
      } else if (tool === TOOLS.ARROW) {
        shape = new fabric.Line([ptr.x, ptr.y, ptr.x, ptr.y], {
          stroke: color, strokeWidth: 2, selectable: true,
          annotationType: TOOLS.ARROW,
        });
      } else if (tool === TOOLS.CORRECT) {
        shape = new fabric.Rect({
          left: ptr.x, top: ptr.y, width: 0, height: 0,
          fill: 'rgba(99,102,241,0.15)',
          stroke: '#6366f1',
          strokeWidth: 2,
          strokeDashArray: [6, 3],
          selectable: false,
          annotationType: TOOLS.CORRECT,
        });
      }

      if (shape) {
        fc.add(shape);
        activeShapeRef.current = shape;
      }
    };

    // Per-canvas sticky-note double-click → enter text edit then re-group
    const onDblClick = (opt) => {
      const target = fc.findTarget(opt.e);
      if (!target || target.annotationType !== TOOLS.STICKY) return;

      const group = target;
      const items = group.getObjects();
      const textObj = items.find(o => o.type === 'i-text');
      if (!textObj) return;

      group.toActiveSelection();
      fc.discardActiveObject();

      const allObjs = fc.getObjects();
      const freeText = allObjs.find(
        o => o.type === 'i-text' &&
             Math.abs(o.left - (group.left + group.width / 2 + (textObj.left || 0))) < 200
      );
      if (!freeText) return;

      fc.setActiveObject(freeText);
      freeText.enterEditing();
      freeText.selectAll();

      freeText.on('editing:exited', () => {
        const bg = allObjs.find(o => o.type === 'rect' && o !== freeText);
        if (!bg) return;
        fc.discardActiveObject();
        const newGroup = new fabric.Group([bg, freeText], {
          selectable: true, hasControls: true,
          annotationType: TOOLS.STICKY,
        });
        fc.remove(bg);
        fc.remove(freeText);
        fc.add(newGroup);
        fc.setActiveObject(newGroup);
        fc.renderAll();
        markPageDirty(page);
      });

      fc.renderAll();
    };

    fc.on('mouse:down', onMouseDown);
    fc.on('mouse:dblclick', onDblClick);

    // Track dirty markers — but suppress while we're hydrating saved data
    const onAdded = () => { if (!fc._hydrating) markPageDirty(page); };
    const onModified = () => { if (!fc._hydrating) markPageDirty(page); };
    const onRemoved = () => { if (!fc._hydrating) markPageDirty(page); };
    fc.on('object:added', onAdded);
    fc.on('object:modified', onModified);
    fc.on('object:removed', onRemoved);
  }, [markPageDirty]);

  // ── Initialise / re-initialise Fabric canvas for one page ───────────────
  const initFabricCanvas = useCallback((page, width, height) => {
    const wrapper = fabricElsRef.current[page];
    if (!wrapper) return;

    // Dispose any previous fabric instance for this page (e.g. on scale change)
    if (fabricCanvasesRef.current[page]) {
      fabricCanvasesRef.current[page].dispose();
      delete fabricCanvasesRef.current[page];
    }
    hydratedPagesRef.current.delete(page);
    wrapper.innerHTML = '';

    // Size the overlay wrapper to match the rendered page (CSS pixels)
    const dpr = window.devicePixelRatio || 1;
    wrapper.style.width = `${width / dpr}px`;
    wrapper.style.height = `${height / dpr}px`;

    const canvasEl = document.createElement('canvas');
    canvasEl.width = width;
    canvasEl.height = height;
    wrapper.appendChild(canvasEl);

    const fc = new fabric.Canvas(canvasEl, {
      width, height,
      selection: true,
      preserveObjectStacking: true,
    });
    fabricCanvasesRef.current[page] = fc;

    bindCanvasHandlers(page, fc);
    applyToolState(fc);
    hydrateCanvas(fc, page);
  }, [bindCanvasHandlers, applyToolState, hydrateCanvas]);

  // ── Per-page render success — capture native width + init fabric ────────
  const handlePageRenderSuccess = useCallback((page) => (renderedPage) => {
    if (page === 1 && nativePdfWidthRef.current == null) {
      const nativeViewport = renderedPage.getViewport({ scale: 1 });
      nativePdfWidthRef.current = nativeViewport.width;
      // Initial fit: scale to container width on first page
      if (scrollContainerRef.current) {
        const containerW = scrollContainerRef.current.clientWidth - 32;
        if (containerW > 0) {
          const fitScale = Math.min(Math.max(containerW / nativeViewport.width, 0.4), 3);
          if (Math.abs(fitScale - scale) > 0.02) {
            setScale(fitScale);
            // The scale change will trigger another render — skip fabric init
            // this pass to avoid creating a canvas at the wrong size.
            return;
          }
        }
      }
    }
    const viewport = renderedPage.getViewport({ scale, rotation: rotation * (Math.PI / 180) });
    const w = Math.floor(viewport.width);
    const h = Math.floor(viewport.height);
    pageDimsRef.current[page] = { width: w, height: h };
    initFabricCanvas(page, w, h);
  }, [scale, rotation, initFabricCanvas]);

  // ── Apply tool state to all canvases when tool/color changes ────────────
  useEffect(() => {
    Object.values(fabricCanvasesRef.current).forEach(fc => applyToolState(fc));
  }, [activeTool, activeColor, applyToolState]);

  // ── Document-level mousemove/mouseup for shape-tool drag with clamping ─
  useEffect(() => {
    const handleMove = (e) => {
      if (!isDrawingRef.current || !activeShapeRef.current) return;
      const startPage = dragStartPageRef.current;
      if (startPage == null) return;
      const fc = fabricCanvasesRef.current[startPage];
      if (!fc) return;
      // Use the fabric upper-canvas DOM element's bounding rect to translate
      // viewport coordinates into the page's fabric coordinate space, then
      // clamp to the page's bounds so the shape never extends past the page.
      const canvasEl = fc.upperCanvasEl || fc.lowerCanvasEl;
      if (!canvasEl) return;
      const rect = canvasEl.getBoundingClientRect();
      const sx = fc.getWidth() / (rect.width || 1);
      const sy = fc.getHeight() / (rect.height || 1);
      const rawX = (e.clientX - rect.left) * sx;
      const rawY = (e.clientY - rect.top) * sy;
      const clampedX = Math.max(0, Math.min(fc.getWidth(), rawX));
      const clampedY = Math.max(0, Math.min(fc.getHeight(), rawY));
      const start = startPointRef.current;
      const shape = activeShapeRef.current;
      const tool = activeToolRef.current;

      if (tool === TOOLS.HIGHLIGHT || tool === TOOLS.REDACT || tool === TOOLS.CORRECT) {
        const left = Math.min(clampedX, start.x);
        const top = Math.min(clampedY, start.y);
        shape.set({
          left, top,
          width: Math.abs(clampedX - start.x),
          height: Math.abs(clampedY - start.y),
        });
      } else if (tool === TOOLS.ARROW) {
        shape.set({ x2: clampedX, y2: clampedY });
      }
      fc.renderAll();
    };

    const handleUp = () => {
      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;
      const startPage = dragStartPageRef.current;
      const shape = activeShapeRef.current;
      const fc = startPage != null ? fabricCanvasesRef.current[startPage] : null;
      const tool = activeToolRef.current;

      // Field correction zone — emit and keep the dashed marquee visible
      if (tool === TOOLS.CORRECT && shape && fc) {
        const zone = {
          page: startPage,
          x: shape.left,
          y: shape.top,
          width: shape.width,
          height: shape.height,
          fabricObject: shape.toJSON(['annotationType']),
        };
        shape.set({ selectable: true });
        fc.renderAll();
        if (onCorrectionDrawn) onCorrectionDrawn(zone, shape, fc);
        activeShapeRef.current = null;
        dragStartPageRef.current = null;
        setActiveTool(TOOLS.SELECT);
        return;
      }

      // Arrow finalize — replace line with line+arrowhead group
      if (tool === TOOLS.ARROW && shape && fc) {
        const line = shape;
        const x1 = line.x1, y1 = line.y1, x2 = line.x2, y2 = line.y2;
        const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
        const headSize = 12;
        const head = new fabric.Triangle({
          left: x2, top: y2,
          width: headSize, height: headSize,
          fill: line.stroke,
          originX: 'center', originY: 'center',
          angle: angle + 90,
        });
        fc.remove(line);
        const arrowGroup = new fabric.Group([
          new fabric.Line([x1, y1, x2, y2], { stroke: line.stroke, strokeWidth: 2 }),
          head,
        ], {
          selectable: true, hasControls: true,
          annotationType: TOOLS.ARROW,
        });
        fc.add(arrowGroup);
        fc.setActiveObject(arrowGroup);
      }

      activeShapeRef.current = null;
      dragStartPageRef.current = null;
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [onCorrectionDrawn]);

  // ── Delete selected object via keyboard ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      // Don't interfere when typing in inputs / IText editing
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const lastPage = lastActiveCanvasPageRef.current;
      const candidates = lastPage != null
        ? [fabricCanvasesRef.current[lastPage], ...Object.values(fabricCanvasesRef.current)]
        : Object.values(fabricCanvasesRef.current);
      for (const fc of candidates) {
        if (!fc) continue;
        const active = fc.getActiveObject();
        if (active) {
          // If the active object is an IText in editing mode, let it consume the key
          if (active.type === 'i-text' && active.isEditing) return;
          fc.remove(active);
          fc.discardActiveObject();
          fc.renderAll();
          return;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── ResizeObserver on scroll container — auto-fit scale to width ────────
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const target = scrollContainerRef.current;
    const ro = new ResizeObserver((entries) => {
      const nativeW = nativePdfWidthRef.current;
      if (!nativeW) return;
      for (const entry of entries) {
        const containerW = entry.contentRect.width - 32;
        if (containerW <= 0) return;
        const newScale = Math.min(Math.max(containerW / nativeW, 0.4), 3);
        setScale(prev => Math.abs(prev - newScale) < 0.02 ? prev : newScale);
      }
    });
    ro.observe(target);
    return () => ro.disconnect();
  }, [numPages]);

  // ── IntersectionObserver: derive currentPage from scroll position ───────
  useEffect(() => {
    if (!scrollContainerRef.current || !numPages) return;
    const root = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting && e.intersectionRatio > 0)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length === 0) return;
        const pageNumber = parseInt(
          visible[0].target.getAttribute('data-page-number') || '1',
          10
        );
        setCurrentPage(prev => {
          if (prev === pageNumber) return prev;
          if (onPageChange) onPageChange(pageNumber);
          return pageNumber;
        });
      },
      { root, threshold: [0.25, 0.5, 0.75] }
    );
    // Observe whichever wrappers exist now; we re-create the observer when
    // numPages changes so later-mounted wrappers are picked up.
    Object.values(pageWrapsRef.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [numPages, onPageChange]);

  // ── Track latest dirtyPages / savePageAnnotations for unmount flush ─────
  const latestDirtyRef = useRef(dirtyPages);
  const latestSaveRef = useRef(savePageAnnotations);
  useEffect(() => { latestDirtyRef.current = dirtyPages; }, [dirtyPages]);
  useEffect(() => { latestSaveRef.current = savePageAnnotations; }, [savePageAnnotations]);

  // ── Cleanup on unmount: dispose canvases, flush pending saves ───────────
  useEffect(() => {
    return () => {
      const save = latestSaveRef.current;
      if (save) {
        Array.from(latestDirtyRef.current).forEach(page => save(page));
      }
      Object.values(fabricCanvasesRef.current).forEach(fc => fc?.dispose());
      fabricCanvasesRef.current = {};
    };
  }, []);

  // ── Scroll a target page into view ──────────────────────────────────────
  const scrollToPage = useCallback((page) => {
    if (!numPages) return;
    const clamped = Math.max(1, Math.min(numPages, page));
    const el = pageWrapsRef.current[clamped];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [numPages]);

  // ── Manual save (Save button) ───────────────────────────────────────────
  const saveAllDirty = useCallback(async () => {
    const pages = Array.from(dirtyPages);
    for (const p of pages) {
      // eslint-disable-next-line no-await-in-loop
      await savePageAnnotations(p);
    }
  }, [dirtyPages, savePageAnnotations]);

  // ── Fit to width (button) ───────────────────────────────────────────────
  const fitToWidth = useCallback(() => {
    if (!scrollContainerRef.current || !nativePdfWidthRef.current) return;
    const containerW = scrollContainerRef.current.clientWidth - 32;
    if (containerW <= 0) return;
    const newScale = containerW / nativePdfWidthRef.current;
    setScale(Math.min(Math.max(newScale, 0.4), 3));
  }, []);

  const isDirty = dirtyPages.size > 0;
  const pageNumbers = Array.from({ length: numPages || 0 }, (_, i) => i + 1);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="spv-root" ref={containerRef}>
      {/* ── Annotation Toolbar ── */}
      <div className="spv-annotation-toolbar">
        <div className="spv-tool-group">
          <button
            className={`spv-tool-btn ${activeTool === TOOLS.SELECT ? 'active' : ''}`}
            onClick={() => setActiveTool(TOOLS.SELECT)}
            title="Select / Move"
          >↖</button>
          <button
            className={`spv-tool-btn ${activeTool === TOOLS.STICKY ? 'active' : ''}`}
            onClick={() => setActiveTool(TOOLS.STICKY)}
            title="Sticky Note"
          >📝</button>
          <button
            className={`spv-tool-btn ${activeTool === TOOLS.HIGHLIGHT ? 'active' : ''}`}
            onClick={() => setActiveTool(TOOLS.HIGHLIGHT)}
            title="Highlight"
          >🖍</button>
          <button
            className={`spv-tool-btn ${activeTool === TOOLS.REDACT ? 'active' : ''}`}
            onClick={() => setActiveTool(TOOLS.REDACT)}
            title="Redact"
          >⬛</button>
          <button
            className={`spv-tool-btn ${activeTool === TOOLS.TEXTBOX ? 'active' : ''}`}
            onClick={() => setActiveTool(TOOLS.TEXTBOX)}
            title="Text Box"
          >T</button>
          <button
            className={`spv-tool-btn ${activeTool === TOOLS.FREEHAND ? 'active' : ''}`}
            onClick={() => setActiveTool(TOOLS.FREEHAND)}
            title="Freehand Draw"
          >✏️</button>
          <button
            className={`spv-tool-btn ${activeTool === TOOLS.ARROW ? 'active' : ''}`}
            onClick={() => setActiveTool(TOOLS.ARROW)}
            title="Arrow"
          >↗</button>
          {/* Field-correction (✎) tool removed: correction is now armed from the
              right-side per-field button in ReviewDetailPage, which drives the
              OCR-word click-to-select overlay via `correctingFieldKey`. The
              TOOLS.CORRECT constant and its fabric draw-a-box handlers remain
              (vestigial) and are intentionally left in place. */}

          {/* Stamp picker */}
          <div className="spv-stamp-wrap">
            <button
              className={`spv-tool-btn ${activeTool === TOOLS.STAMP ? 'active' : ''}`}
              onClick={() => { setActiveTool(TOOLS.STAMP); setShowStampPicker(p => !p); }}
              title="Stamp"
            >🔖</button>
            {showStampPicker && (
              <div className="spv-stamp-picker">
                {Object.entries(STAMP_CONFIGS).map(([key, cfg]) => (
                  <button
                    key={key}
                    className={`spv-stamp-option ${activeStamp === key ? 'selected' : ''}`}
                    style={{ backgroundColor: cfg.color, color: cfg.textColor }}
                    onClick={() => { setActiveStamp(key); setShowStampPicker(false); }}
                  >{cfg.label}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Color swatches */}
        <div className="spv-tool-group spv-colors">
          {Object.entries(ANNOTATION_COLORS).map(([name, hex]) => (
            <button
              key={name}
              className={`spv-color-swatch ${activeColor === name ? 'active' : ''}`}
              style={{ backgroundColor: hex }}
              onClick={() => setActiveColor(name)}
              title={name}
            />
          ))}
        </div>

        {/* Save indicator */}
        <div className="spv-tool-group spv-save-status">
          {saving && <span className="spv-saving">Saving…</span>}
          {/* A failed save keeps the page dirty, so the Save button stays
              available to retry; the message says the work is NOT stored
              rather than letting the toolbar look clean. */}
          {!saving && saveError && (
            <span className="spv-save-error" title={saveError}>⚠ {saveError}</span>
          )}
          {!saving && isDirty && (
            <button className="spv-save-btn" onClick={saveAllDirty}>
              {saveError ? 'Retry save' : 'Save'}
            </button>
          )}
          {!saving && !isDirty && !saveError && <span className="spv-saved">✓ Saved</span>}
        </div>
      </div>

      {/* ── Navigation Toolbar ── */}
      <div className="spv-nav-toolbar">
        <button
          onClick={() => scrollToPage(currentPage - 1)}
          disabled={currentPage <= 1}
        >‹</button>
        <span>{currentPage} / {numPages || '—'}</span>
        <button
          onClick={() => scrollToPage(currentPage + 1)}
          disabled={!numPages || currentPage >= numPages}
        >›</button>
        <span className="spv-sep" />
        <button onClick={() => setScale(s => Math.max(0.5, s - 0.1))}>−</button>
        <span>{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.min(3, s + 0.1))}>+</button>
        <button onClick={fitToWidth}>Fit</button>
        <button onClick={() => setRotation(r => (r + 90) % 360)}>↻</button>
        <span className="spv-sep" />
        {onSplitDelete && <button onClick={onSplitDelete}>✂ Split / Delete</button>}
        {onAppend && <button onClick={onAppend}>⊕ Append</button>}
      </div>

      {/* ── Thumbnail Sidebar + Main Viewer ── */}
      <div className="spv-body">
        <div className="spv-thumbs">
          <Document file={fileUrl} options={PDF_DOCUMENT_OPTIONS}>
            {pageNumbers.map(pg => {
              const role = pageRoles[pg];
              const roleDef = PAGE_ROLES.find(r => r.key === role);
              return (
                <div
                  key={pg}
                  className={`spv-thumb ${pg === currentPage ? 'active' : ''}`}
                  onClick={() => scrollToPage(pg)}
                >
                  <Page
                    pageNumber={pg}
                    width={80}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                  <div className="spv-thumb-label">{pg}</div>
                  <div
                    className={`spv-thumb-role ${roleDef ? 'has-role' : ''}`}
                    title={roleDef ? roleDef.label : 'Set page role'}
                    onClick={e => {
                      e.stopPropagation();
                      setShowRolePicker(showRolePicker === pg ? null : pg);
                    }}
                  >
                    {roleDef ? `${roleDef.icon} ${roleDef.label}` : '+ Role'}
                  </div>
                  {showRolePicker === pg && (
                    <div className="spv-role-picker" onClick={e => e.stopPropagation()}>
                      {PAGE_ROLES.map(r => (
                        <button
                          key={r.key}
                          className={`spv-role-option ${role === r.key ? 'selected' : ''}`}
                          onClick={() => {
                            savePageRole(pg, r.key);
                            setShowRolePicker(null);
                          }}
                        >
                          {r.icon} {r.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Document>
        </div>

        {/* Continuous-scroll main viewer */}
        <div className="spv-viewer-main" ref={scrollContainerRef}>
          {/* Armed-state banner: persistent, non-blocking guidance while a
              field is armed for click-to-select. Overlaid at the top of the
              scroll container (sticky) so it stays visible as the user scrolls
              to the value; clears automatically when correctingFieldKey → null
              (confirm/cancel/click). On a rotated page the overlay can't render,
              so the copy switches to rotate-or-type guidance. */}
          {correctingFieldKey != null && (
            <div className="spv-correcting-banner" role="status">
              {rotation === 0 ? (
                <>
                  <span className="spv-correcting-banner-main">
                    Click the value for <strong>“{correctingFieldLabel}”</strong> in the document
                  </span>
                  <span className="spv-correcting-banner-sub">
                    or press the field button again to type it
                  </span>
                </>
              ) : (
                <span className="spv-correcting-banner-main">
                  Rotate the page to 0° to click the value for <strong>“{correctingFieldLabel}”</strong>, or press the field button again to type
                </span>
              )}
            </div>
          )}
          <div className="spv-pages-stack">
            <Document
              file={fileUrl}
              options={PDF_DOCUMENT_OPTIONS}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            >
              {pageNumbers.map(pg => (
                <div
                  key={pg}
                  className="spv-page-wrap"
                  data-page-number={pg}
                  ref={(el) => {
                    pageWrapsRef.current[pg] = el;
                    getPageSurfaceRefObject(pg).current = el;
                  }}
                >
                  <Page
                    pageNumber={pg}
                    scale={scale}
                    rotate={rotation}
                    onRenderSuccess={handlePageRenderSuccess(pg)}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                  {/* Fabric annotation overlay — one per page */}
                  <div
                    ref={(el) => { fabricElsRef.current[pg] = el; }}
                    className="spv-fabric-overlay"
                  />
                  {/* OCR word overlay (click-to-correct) — one per page */}
                  {correctingFieldKey != null && rotation === 0 && (
                    <OcrWordOverlay
                      documentId={documentId}
                      currentPageNumber={pg}
                      pageSurfaceRef={getPageSurfaceRefObject(pg)}
                      /* no engine filter: see OcrWordOverlay — filtering to paddle rendered
                         an empty overlay for every text-layer-acquired document */
                      showOverlay={true}
                      clickToIndex={true}
                      enrichSingleClick={true}
                      /* Review corrections only: a click on a phrase token sends the
                         value after its label. Other overlay users read the full text. */
                      splitInlineLabels={true}
                      onWordClick={(word) => {
                        if (!onTokenClicked || !word?.bbox) return;
                        onTokenClicked({
                          text: word.text,
                          bbox: word.bbox,
                          page: pg,
                          anchorText: word.anchorText ?? null,
                          anchorBbox: word.anchorBbox ?? null,
                          valuePosition: word.valuePosition ?? 'region_only',
                        });
                      }}
                    />
                  )}
                </div>
              ))}
            </Document>
          </div>
        </div>
      </div>
    </div>
  );
}
